import {
  FEE_MODULE_ABI,
  IDENTITY_ADAPTER_ABI,
  IDENTITY_REGISTRY_ABI,
  MOCK_ERC20_ABI,
  POOL_MODIFY_LIQUIDITY_TEST_ABI,
  POOL_SWAP_TEST_ABI,
  STATE_VIEW_ABI,
  V2_PAIR_ABI,
  V2_ROUTER_ABI,
  V3_FACTORY_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
  V3_POOL_ABI,
  V3_SWAP_ROUTER_ABI,
  VAULT_CORE_ABI,
  VAULT_FACTORY_ABI,
  WETH9_ABI,
} from "./abis";

import type { DecodedTx } from "./tx-decoder";
import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AbiEntry {
  protocol: DecodedTx["protocol"];
  abi: readonly unknown[];
  formatAction: (
    functionName: string,
    args: readonly unknown[],
    knownAddresses: Record<Address, string>,
    contractAddress?: Address,
  ) => {
    action: string;
    details: string;
  };
}

// ---------------------------------------------------------------------------
// Action classification
// ---------------------------------------------------------------------------

export type ActionCategory =
  | "value-add"
  | "swap"
  | "admin"
  | "destructive"
  | "neutral";

export function classifyAction(action: string): ActionCategory {
  const lower = action.toLowerCase();
  if (lower.includes("swap")) return "swap";
  if (
    lower.includes("mint") ||
    lower.includes("add liquidity") ||
    lower.includes("deposit") ||
    lower.includes("modify liquidity")
  )
    return "value-add";
  if (
    lower.includes("approve") ||
    lower.includes("create") ||
    lower.includes("register") ||
    lower.includes("initialize")
  )
    return "admin";
  if (
    lower.includes("remove") ||
    lower.includes("burn") ||
    lower.includes("withdraw")
  )
    return "destructive";
  return "neutral";
}

export const ACTION_CATEGORY_COLORS: Record<ActionCategory, string> = {
  "value-add": "text-green-400",
  swap: "text-blue-400",
  admin: "text-amber-400",
  destructive: "text-red-400",
  neutral: "text-text-dim",
};

// ---------------------------------------------------------------------------
// Helpers used by formatAction closures
// ---------------------------------------------------------------------------

function truncAddr(address: string, known: Record<Address, string>): string {
  const lower = address.toLowerCase() as Address;
  for (const [addr, name] of Object.entries(known)) {
    if (addr.toLowerCase() === lower) return name;
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

const TOKEN_DECIMALS: Record<string, number> = {
  WETH: 18,
  ETH: 18,
  USDC: 6,
  USDT: 6,
  DAI: 18,
  WBTC: 8,
  UNI: 18,
};

function resolveTokenSymbol(
  contractAddress: Address | undefined,
  known: Record<Address, string>,
): string | undefined {
  if (!contractAddress) return undefined;
  const lower = contractAddress.toLowerCase();
  for (const [addr, name] of Object.entries(known)) {
    if (addr.toLowerCase() === lower) return name;
  }
  return undefined;
}

function formatFeePercent(fee: number): string {
  const pct = fee / 10000;
  if (pct % 1 === 0) return String(pct);
  return pct.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function formatAmount(value: bigint | undefined): string {
  if (value === undefined) return "?";
  const num = Number(value);
  if (num === 0) return "0";
  if (num >= 1e18) return (num / 1e18).toFixed(4);
  if (num >= 1e8) return (num / 1e6).toFixed(2);
  return num.toString();
}

function formatAmountWithSymbol(
  value: bigint | undefined,
  symbol?: string,
): string {
  if (value === undefined) return "?";
  if (symbol) {
    const decimals = TOKEN_DECIMALS[symbol];
    if (decimals !== undefined) {
      const num = Number(value) / 10 ** decimals;
      if (num === 0) return "0";
      if (Math.abs(num) >= 1)
        return num.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        });
      if (Math.abs(num) >= 0.0001)
        return num.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 6,
        });
      return num.toExponential(2);
    }
  }
  return formatAmount(value);
}

// ---------------------------------------------------------------------------
// Transaction calldata ABI entries (tried in order by decodeTx)
// ---------------------------------------------------------------------------

export const ABI_ENTRIES: AbiEntry[] = [
  // V2 Router
  {
    protocol: "V2",
    abi: V2_ROUTER_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "swapExactTokensForTokens") {
        const [amountIn, , path] = args as [bigint, bigint, Address[]];
        const from = truncAddr(path[0] ?? ("0x" as Address), known);
        const to = truncAddr(path[path.length - 1] ?? ("0x" as Address), known);
        return {
          action: "Swap",
          details: `${formatAmountWithSymbol(amountIn, from)} ${from} \u2192 ${to}`,
        };
      }
      if (functionName === "addLiquidity") {
        const [tokenA, tokenB] = args as [Address, Address];
        return {
          action: "Add Liquidity",
          details: `${truncAddr(tokenA, known)} / ${truncAddr(tokenB, known)}`,
        };
      }
      if (functionName === "addLiquidityETH") {
        const [token] = args as [Address];
        return {
          action: "Add Liquidity ETH",
          details: `ETH / ${truncAddr(token, known)}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // V3 Swap Router
  {
    protocol: "V3",
    abi: V3_SWAP_ROUTER_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "exactInputSingle") {
        const [params] = args as [
          { tokenIn: Address; tokenOut: Address; amountIn: bigint },
        ];
        const tokenIn = truncAddr(params.tokenIn, known);
        const tokenOut = truncAddr(params.tokenOut, known);
        return {
          action: "Swap",
          details: `${formatAmountWithSymbol(params.amountIn, tokenIn)} ${tokenIn} \u2192 ${tokenOut}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // V3 NFT Position Manager
  {
    protocol: "V3",
    abi: V3_NFT_POSITION_MANAGER_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "mint") {
        const [params] = args as [
          { token0: Address; token1: Address; fee: number },
        ];
        return {
          action: "Mint LP",
          details: `${truncAddr(params.token0, known)}/${truncAddr(params.token1, known)} (${formatFeePercent(params.fee)}% fee)`,
        };
      }
      if (functionName === "createAndInitializePoolIfNecessary") {
        const [token0, token1, fee] = args as [Address, Address, number];
        return {
          action: "Create Pool",
          details: `${truncAddr(token0, known)}/${truncAddr(token1, known)} (${formatFeePercent(fee)}% fee)`,
        };
      }
      if (functionName === "balanceOf") {
        const [owner] = args as [Address];
        return { action: "Balance Of", details: truncAddr(owner, known) };
      }
      if (functionName === "positions") {
        const [tokenId] = args as [bigint];
        return { action: "Get Position", details: `#${tokenId.toString()}` };
      }
      if (functionName === "tokenOfOwnerByIndex") {
        return { action: "Token By Index", details: "" };
      }
      return { action: functionName, details: "" };
    },
  },
  // V4 Pool Swap Test
  {
    protocol: "V4",
    abi: POOL_SWAP_TEST_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "swap") {
        const [key, params] = args as [
          { currency0: Address; currency1: Address },
          { zeroForOne: boolean; amountSpecified: bigint },
        ];
        const dir = params.zeroForOne ? "\u2192" : "\u2190";
        return {
          action: "Swap",
          details: `${truncAddr(key.currency0, known)} ${dir} ${truncAddr(key.currency1, known)}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // V4 Pool Modify Liquidity Test
  {
    protocol: "V4",
    abi: POOL_MODIFY_LIQUIDITY_TEST_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "modifyLiquidity") {
        const [key] = args as [{ currency0: Address; currency1: Address }];
        return {
          action: "Modify Liquidity",
          details: `${truncAddr(key.currency0, known)} / ${truncAddr(key.currency1, known)}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // ERC20
  {
    protocol: "ERC20",
    abi: MOCK_ERC20_ABI,
    formatAction: (functionName, args, known, contractAddress) => {
      const symbol = resolveTokenSymbol(contractAddress, known);
      if (functionName === "transfer") {
        const [to, amount] = args as [Address, bigint];
        const amt = formatAmountWithSymbol(amount, symbol);
        const label = symbol ? `${amt} ${symbol}` : amt;
        return {
          action: "Transfer",
          details: `${label} \u2192 ${truncAddr(to, known)}`,
        };
      }
      if (functionName === "transferFrom") {
        const [from, to, amount] = args as [Address, Address, bigint];
        const amt = formatAmountWithSymbol(amount, symbol);
        const label = symbol ? `${amt} ${symbol}` : amt;
        return {
          action: "Transfer From",
          details: `${label} ${truncAddr(from, known)} \u2192 ${truncAddr(to, known)}`,
        };
      }
      if (functionName === "approve") {
        const [spender] = args as [Address];
        return {
          action: "Approve",
          details: `spender: ${truncAddr(spender, known)}`,
        };
      }
      if (functionName === "mint") {
        const [to, amount] = args as [Address, bigint];
        const amt = formatAmountWithSymbol(amount, symbol);
        const label = symbol ? `${amt} ${symbol}` : amt;
        return {
          action: "Mint",
          details: `${label} to ${truncAddr(to, known)}`,
        };
      }
      if (functionName === "burn") {
        const [amount] = args as [bigint];
        const amt = formatAmountWithSymbol(amount, symbol);
        return { action: "Burn", details: symbol ? `${amt} ${symbol}` : amt };
      }
      return { action: functionName, details: "" };
    },
  },
  // ERC-8004 Identity Registry
  {
    protocol: "ERC-8004",
    abi: IDENTITY_REGISTRY_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "register") {
        const [agent, uri] = args as [Address, string];
        return {
          action: "Register Agent",
          details: `${truncAddr(agent, known)} (${uri.slice(0, 30)})`,
        };
      }
      if (functionName === "updateIdentity") {
        const [uri] = args as [string];
        return {
          action: "Update Identity",
          details: uri.slice(0, 40),
        };
      }
      if (functionName === "isRegistered") {
        const [agent] = args as [Address];
        return { action: "Is Registered", details: truncAddr(agent, known) };
      }
      if (functionName === "getIdentity") {
        const [agent] = args as [Address];
        return { action: "Get Identity", details: truncAddr(agent, known) };
      }
      if (functionName === "getAgentId") {
        const [agent] = args as [Address];
        return { action: "Get Agent ID", details: truncAddr(agent, known) };
      }
      if (functionName === "getAgentAddress") {
        const [agentId] = args as [bigint];
        return {
          action: "Get Agent Address",
          details: `#${agentId.toString()}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // Vault Factory
  {
    protocol: "Vault",
    abi: VAULT_FACTORY_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "createVault") {
        const [config] = args as [
          {
            baseAsset: Address;
            creatorAgentId: bigint;
            managementFeeBps: number;
            performanceFeeBps: number;
          },
        ];
        return {
          action: "Create Vault",
          details: `agent #${config.creatorAgentId.toString()} asset: ${truncAddr(config.baseAsset, known)} (${String(config.managementFeeBps)}/${String(config.performanceFeeBps)} bps)`,
        };
      }
      if (functionName === "vaultCount") {
        return { action: "Vault Count", details: "" };
      }
      if (functionName === "isVault") {
        const [vault] = args as [Address];
        return { action: "Is Vault", details: truncAddr(vault, known) };
      }
      if (functionName === "getVaults") {
        const [offset, limit] = args as [bigint, bigint];
        return {
          action: "Get Vaults",
          details: `offset: ${offset.toString()}, limit: ${limit.toString()}`,
        };
      }
      if (functionName === "getVaultsByCreator") {
        const [agentId] = args as [bigint];
        return {
          action: "Get Vaults By Creator",
          details: `agent #${agentId.toString()}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // Vault Core (ERC-4626)
  {
    protocol: "Vault",
    abi: VAULT_CORE_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "deposit") {
        const [assets, receiver] = args as [bigint, Address];
        return {
          action: "Deposit",
          details: `${formatAmount(assets)} to ${truncAddr(receiver, known)}`,
        };
      }
      if (functionName === "withdraw") {
        const [assets, receiver] = args as [bigint, Address];
        return {
          action: "Withdraw",
          details: `${formatAmount(assets)} to ${truncAddr(receiver, known)}`,
        };
      }
      if (functionName === "redeem") {
        const [shares, receiver] = args as [bigint, Address];
        return {
          action: "Redeem",
          details: `${formatAmount(shares)} shares to ${truncAddr(receiver, known)}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // Fee Module
  {
    protocol: "Vault",
    abi: FEE_MODULE_ABI,
    formatAction: (functionName, args, known) => {
      if (functionName === "report") {
        const [vault] = args as [Address];
        return {
          action: "Fee Report",
          details: `vault: ${truncAddr(vault, known)}`,
        };
      }
      if (functionName === "getAccruedFees") {
        const [vault] = args as [Address];
        return {
          action: "Get Accrued Fees",
          details: truncAddr(vault, known),
        };
      }
      if (functionName === "computeEffectiveFee") {
        const [baseFee, tier, isManagement] = args as [number, number, boolean];
        return {
          action: "Compute Fee",
          details: `${String(baseFee)} bps, tier ${String(tier)}, ${isManagement ? "mgmt" : "perf"}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
  // Identity Registry Adapter
  {
    protocol: "Vault",
    abi: IDENTITY_ADAPTER_ABI,
    formatAction: (functionName, args, _known) => {
      if (functionName === "isValidAgent") {
        const [agentId] = args as [bigint];
        return { action: "Is Valid Agent", details: `#${agentId.toString()}` };
      }
      if (functionName === "getAgentWallet") {
        const [agentId] = args as [bigint];
        return {
          action: "Get Agent Wallet",
          details: `#${agentId.toString()}`,
        };
      }
      if (functionName === "getEffectiveReputation") {
        const [agentId] = args as [bigint];
        return {
          action: "Get Reputation",
          details: `agent #${agentId.toString()}`,
        };
      }
      if (functionName === "isCredentialFrozen") {
        const [agentId] = args as [bigint];
        return {
          action: "Is Frozen",
          details: `agent #${agentId.toString()}`,
        };
      }
      return { action: functionName, details: "" };
    },
  },
];

// ---------------------------------------------------------------------------
// Event log ABI list (tried in order by tryDecodeLogs)
// ---------------------------------------------------------------------------

export const LOG_ABIS = [
  MOCK_ERC20_ABI,
  V2_PAIR_ABI,
  V3_FACTORY_ABI,
  V3_POOL_ABI,
  V3_NFT_POSITION_MANAGER_ABI,
  WETH9_ABI,
  STATE_VIEW_ABI,
  VAULT_FACTORY_ABI,
  VAULT_CORE_ABI,
  FEE_MODULE_ABI,
] as const;
